import { useReduxState } from 'hooks';
import { useFormApi, useFormState } from 'informed';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getDefaultAccessAndVisibilityLabels } from 'lib/accessAndVisibility';
import {
  Checkbox,
  FormGroup,
  NumberStepper,
  RadioGroup
} from 'components/common/form';

import type { RadioGroupOption } from 'components/common/form/RadioGroup';

type VisibilityCriteriaKey = 'groups' | 'sso' | 'userAttributes' | 'users';

export type AccessAndVisibilityFormElements<
  TCriteriaKey extends VisibilityCriteriaKey = VisibilityCriteriaKey
> = {
  participationLimit?: number;
  restrictions?: 'none' | 'user-limit' | 'request-access';
  visibility: 'anyone' | 'limited' | 'private' | 'public';
  visibilityCriteria: Record<TCriteriaKey, boolean>;
};

interface Props {
  // definition props
  hasPendingRequests: boolean;
  restrictions?: Record<
    AccessAndVisibilityFormElements['restrictions'],
    boolean
  >;
  visibility?: Record<AccessAndVisibilityFormElements['visibility'], boolean>;
  visibilityCriteria?: Partial<Record<VisibilityCriteriaKey, boolean>>;
  // handlers
  onRestrictionChange?: (
    restrcition: AccessAndVisibilityFormElements['restrictions']
  ) => Promise<void> | void;
  onVisibilityOptionChange?: (
    visibilityOption: AccessAndVisibilityFormElements['visibility']
  ) => Promise<void> | void;
  // form options
  customLabels?: Partial<
    ReturnType<typeof getDefaultAccessAndVisibilityLabels>
  >;
}

const AccessAndVisibility = ({
  // definitions
  hasPendingRequests,
  restrictions = { none: true, 'user-limit': true, 'request-access': true },
  visibility = { anyone: true, limited: true, private: true, public: false },
  visibilityCriteria = {
    sso: true,
    groups: true,
    userAttributes: true,
    users: false
  },
  // handlers
  onRestrictionChange,
  onVisibilityOptionChange,
  // form options
  customLabels = {}
}: Props) => {
  const {
    messages,
    serverConfig: { isDemoEnvironment }
  } = useReduxState();

  const formApi = useFormApi<AccessAndVisibilityFormElements>();
  const { errors, values } = useFormState<AccessAndVisibilityFormElements>();

  const labels = {
    ...getDefaultAccessAndVisibilityLabels({ messages }),
    ...customLabels
  };

  const handleVisibilityChange = (
    value: AccessAndVisibilityFormElements['visibility']
  ) => {
    if (value !== 'limited') {
      visibilityCriteriaOptions.forEach((criteria) => {
        formApi.setValue(`visibilityCriteria.${criteria.value}`, false);
        formApi.setError('visibilityCriteria.userAttributes', undefined);
      });
    }
    if (value === 'private') {
      formApi.setValue('restrictions', 'none');
      if (restrictions['user-limit']) formApi.setValue('participationLimit', 0);
    }

    if (onVisibilityOptionChange) onVisibilityOptionChange(value);
  };

  const handleRestrictionsChange = (
    value: AccessAndVisibilityFormElements['restrictions']
  ) => {
    if (restrictions['user-limit']) {
      formApi.setValue('participationLimit', value === 'user-limit' ? 1 : 0);
    }
    if (onRestrictionChange) onRestrictionChange(value);
  };

  const CRITERIA_KEYS: VisibilityCriteriaKey[] = [
    'groups',
    'sso',
    'userAttributes',
    'users'
  ];

  const validateAtLeastOneCriterion =
    (currentKey: VisibilityCriteriaKey) =>
    (value: boolean, values: AccessAndVisibilityFormElements) => {
      const nextCriteria = {
        ...values.visibilityCriteria,
        [currentKey]: value
      };

      const isInvalid =
        values.visibility === 'limited' &&
        CRITERIA_KEYS.every((key) => !nextCriteria[key]);

      if (currentKey !== 'userAttributes') {
        formApi.setError(
          'visibilityCriteria.userAttributes',
          isInvalid ? 'error' : undefined
        );
        return undefined;
      }

      return isInvalid ? 'error' : undefined;
    };

  const pendingRequestsDisabled =
    values?.restrictions === 'request-access' && hasPendingRequests;

  const visibilityCriteriaOptions: {
    label: string;
    value: VisibilityCriteriaKey;
    labelTooltip?: string;
  }[] = [
    ...(visibilityCriteria.sso
      ? [
          {
            label: labels.visibilityCriteriaOptionSSO,
            value: 'sso' as const
          }
        ]
      : []),
    ...(visibilityCriteria.users
      ? [
          {
            label: labels.visibilityCriteriaOptionUsers,
            labelTooltip: labels.visibilityCriteriaOptionUsersTooltip,
            value: 'users' as const
          }
        ]
      : []),
    ...(visibilityCriteria.groups
      ? [
          {
            label: labels.visibilityCriteriaOptionGroups,
            labelTooltip: labels.visibilityCriteriaOptionGroupsTooltip,
            value: 'groups' as const
          }
        ]
      : []),
    ...(visibilityCriteria.userAttributes
      ? [
          {
            label: labels.visibilityCriteriaOptionUserAttributes,
            labelTooltip: labels.visibilityCriteriaOptionUserAttributesTooltip,
            value: 'userAttributes' as const
          }
        ]
      : [])
  ];

  const visibilityOptions = [
    ...(visibility.public
      ? [
          {
            id: 'public',
            label: labels.visibilityOptionPublic,
            tooltip: messages.accessAndVisibilityVisibilityOptionPublicTooltip,
            value: 'public'
          }
        ]
      : []),
    ...(visibility.private
      ? [
          {
            disabled: pendingRequestsDisabled,
            id: 'private',
            label: labels.visibilityOptionPrivate,
            tooltip: pendingRequestsDisabled
              ? messages.accessRequestsPending
              : null,
            value: 'private'
          }
        ]
      : []),
    ...(visibility.anyone
      ? [
          {
            disabled: isDemoEnvironment,
            id: 'all',
            label: labels.visibilityOptionAnyone,
            value: 'anyone',
            ...(isDemoEnvironment
              ? { tooltip: messages.commonDisabledInDemo }
              : {})
          }
        ]
      : []),
    ...(visibility.limited && visibilityCriteriaOptions.length > 0
      ? [
          {
            disabled: isDemoEnvironment,
            id: 'limited',
            label: labels.visibilityOptionLimited,
            value: 'limited',
            ...(isDemoEnvironment
              ? { tooltip: messages.commonDisabledInDemo }
              : {})
          }
        ]
      : [])
  ] as RadioGroupOption[];

  const restrictionsOptions = [
    ...(restrictions.none
      ? [
          {
            id: 'none',
            label: labels.restrictionOptionNone,
            value: 'none'
          }
        ]
      : []),
    ...(restrictions['user-limit']
      ? [
          {
            id: 'user-limit',
            label: labels.restrictionOptionUserLimit,
            value: 'user-limit'
          }
        ]
      : []),
    ...(restrictions['request-access']
      ? [
          {
            disabled: values.visibility === 'public',
            id: 'user-request',
            label: labels.restrictionOptionUserRequest,
            value: 'request-access'
          }
        ]
      : [])
  ] as RadioGroupOption[];

  if (pendingRequestsDisabled) {
    restrictionsOptions.forEach((option) => {
      option.disabled = true;
      option.tooltip = messages.accessRequestsPending;
    });
  }

  return !visibilityOptions.length ? null : (
    <>
      <FormGroup
        label={labels.headerUserAccess}
        labelHelpText={labels.headerUserAccessHelpText}
      >
        <RadioGroup
          id="visibility"
          field="visibility"
          onChange={handleVisibilityChange}
          options={visibilityOptions}
        />
      </FormGroup>

      {visibilityCriteriaOptions.length > 0 ? (
        <div
          style={{
            paddingLeft: 22,
            marginTop: 7
          }}
        >
          {visibilityCriteriaOptions.map((criteria) => (
            <Checkbox
              key={criteria.value}
              disabled={values.visibility !== 'limited'}
              error={
                criteria.value === 'userAttributes' &&
                errors.visibilityCriteria?.userAttributes
                  ? labels.visibilityCriteriaOptionError
                  : undefined
              }
              id={`visibility-criteria-${criteria.value}`}
              field={`visibilityCriteria.${criteria.value}`}
              label={criteria.label}
              labelTooltip={criteria.labelTooltip}
              style={{ marginTop: 7 }}
              validate={validateAtLeastOneCriterion(criteria.value)}
              validateOnChange={true}
            />
          ))}
        </div>
      ) : null}

      {restrictionsOptions.length > 0 ? (
        <div
          style={{
            display: values.visibility === 'private' ? 'none' : 'block',
            marginTop: 15
          }}
        >
          <FormGroup label={labels.headerRestrictions}>
            <RadioGroup
              id="restrictions"
              field="restrictions"
              onChange={handleRestrictionsChange}
              options={restrictionsOptions}
            />
          </FormGroup>
        </div>
      ) : null}

      <div
        style={{
          display: values.restrictions === 'user-limit' ? 'block' : 'none',
          marginTop: 15
        }}
      >
        <FormGroup
          label={labels.headerUserLimits}
          labelHelpText={labels.headerUserLimitsHelpText}
        >
          <NumberStepper
            id="participationLimit"
            field="participationLimit"
            label={getMessageWithPlaceholders({
              message: messages.usersNumberOfUsers,
              placeholders: { numberOfUsers: '' }
            })}
            max={100000}
            min={1}
            precision={1}
            stepValue={1}
          />
        </FormGroup>
      </div>
    </>
  );
};

export default AccessAndVisibility;
